import { useEffect } from 'react';
import toast from 'react-hot-toast';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { useDelayedSkeleton } from '@/common/hooks/useDelayedSkeleton';
import { getCRMDashboardStats } from '../../api';
import contactIcon from '../../assets/contact-icon.png';
import userAddIcon from '../../assets/user-add.png';
import phoneCheckIcon from '../../assets/phone-check.png';
import trendUpIcon from '../../assets/TrendUp.png';
import trendDownIcon from '../../assets/TrendDown.png';
import funnelIcon from '../../assets/funnelicon.png';

function Sparkline({ color, gradientId }) {
  return (
    <svg
      viewBox="0 0 160 72"
      className="w-full h-full"
      preserveAspectRatio="none"
      aria-hidden
    >
      <defs>
        <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.35" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <path
        d="M0 48 C20 44, 28 28, 42 32 C56 36, 62 52, 78 46 C94 40, 102 18, 118 24 C134 30, 142 42, 160 28 L160 72 L0 72 Z"
        fill={`url(#${gradientId})`}
      />
      <path
        d="M0 48 C20 44, 28 28, 42 32 C56 36, 62 52, 78 46 C94 40, 102 18, 118 24 C134 30, 142 42, 160 28"
        fill="none"
        stroke={color}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  );
}

import { useFormatters } from '../../hooks/useFormatters';

/**
 * Equal-width cards with even gaps.
 * md: 2-up; last orphan stretches full width. lg+: 3 equal flex columns.
 */
export const CARD_WIDTH =
  'w-full min-w-0 md:w-[calc(50%-0.5rem)] md:max-lg:last:odd:w-full lg:w-auto lg:flex-1';
export const METRICS_GRID =
  'flex w-full flex-col gap-4 md:flex-row md:flex-wrap lg:flex-nowrap';

export function SummaryCard({ title, value, iconSrc, accent, iconBg }) {
  return (
    <div
      className={`relative flex items-center gap-4 bg-white rounded-[12px] shadow-[0_1px_3px_rgba(0,0,0,0.06)] px-5 py-5 overflow-hidden min-h-[112px] ${CARD_WIDTH}`}
    >
      <span
        className="absolute left-0 top-3 bottom-3 w-[4px] rounded-r-full"
        style={{ backgroundColor: accent }}
      />
      <div
        className="shrink-0 w-14 h-14 rounded-[12px] flex items-center justify-center"
        style={{ backgroundColor: iconBg }}
      >
        <img src={iconSrc} alt="" className="w-8 h-8 object-contain" />
      </div>
      <div className="min-w-0 flex flex-col gap-1">
        <p className="text-[16px] leading-5 font-medium text-[#868686]">
          {title}
        </p>
        <p className="text-[28px] leading-8 font-semibold text-[#1F2241] tracking-tight">
          {value}
        </p>
      </div>
    </div>
  );
}

function RevenueCard({
  title,
  value,
  iconSrc,
  iconBg,
  sparkColor,
  gradientId,
}) {
  return (
    <div
      className={`relative flex items-stretch bg-white rounded-[12px] shadow-[0_1px_3px_rgba(0,0,0,0.06)] pl-5 pr-0 py-5 overflow-hidden min-h-[132px] ${CARD_WIDTH}`}
    >
      <div className="relative z-10 flex w-[46%] min-w-0 shrink-0 flex-col gap-3 bg-white pr-3">
        <div
          className="shrink-0 w-14 h-14 rounded-[12px] flex items-center justify-center"
          style={{ backgroundColor: iconBg }}
        >
          <img src={iconSrc} alt="" className="w-8 h-8 object-contain" />
        </div>
        <div className="min-w-0 flex flex-col gap-1">
          <p className="text-[16px] leading-5 font-medium text-[#868686]">
            {title}
          </p>
          <p className="text-[24px] sm:text-[26px] leading-8 font-semibold text-[#1F2241] tracking-tight">
            {value}
          </p>
        </div>
      </div>
      <div className="relative min-w-0 flex-1 self-stretch pointer-events-none [mask-image:linear-gradient(to_right,transparent_0%,#000_12%)] [-webkit-mask-image:linear-gradient(to_right,transparent_0%,#000_12%)]">
        <Sparkline color={sparkColor} gradientId={gradientId} />
      </div>
    </div>
  );
}

export function CRMDashboardMetrics() {
  const { formatCurrency, formatNumber: formatCount } = useFormatters();

  const getCompanyId = () => {
    try {
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        const userObj = JSON.parse(storedUser);
        return userObj?.company_id || localStorage.getItem('company_id');
      }
    } catch (e) {
      console.error('Failed to parse user object from localStorage', e);
    }
    return localStorage.getItem('company_id');
  };

  const companyId = getCompanyId();

  const {
    data,
    isLoading: isInitialLoading,
    isFetching,
    isError,
    error,
  } = useQuery({
    queryKey: ['crm-dashboard-stats', companyId],
    queryFn: () => getCRMDashboardStats(companyId),
    enabled: !!companyId,
    placeholderData: keepPreviousData,
  });

  const showDelayedSkeleton = useDelayedSkeleton(
    isFetching && data !== undefined,
    300,
    500,
  );
  const showSkeleton = isInitialLoading || showDelayedSkeleton;

  useEffect(() => {
    if (isError) {
      const apiErrorMessage =
        error?.response?.data?.detail ||
        error?.response?.data?.message ||
        error?.message ||
        'Failed to load metrics statistics from the server.';
      toast.error(apiErrorMessage);
    }
  }, [isError, error]);

  if (showSkeleton || !data) {
    return (
      <div className={METRICS_GRID}>
        {[1, 2, 3, 4, 5, 6].map((idx) => (
          <div
            key={idx}
            className={`h-[112px] rounded-[12px] bg-white animate-pulse shadow-[0_1px_3px_rgba(0,0,0,0.06)] ${CARD_WIDTH}`}
          />
        ))}
      </div>
    );
  }

  const summaryCards = [
    {
      title: 'Total Contacts',
      value: formatCount(data?.total_contacts),
      iconSrc: contactIcon,
      accent: '#0088FF',
      iconBg: 'rgba(0, 136, 255, 0.12)',
    },
    {
      title: 'Total New Leads',
      value: formatCount(data?.total_new_leads),
      iconSrc: userAddIcon,
      accent: '#FF610E',
      iconBg: 'rgba(255, 97, 14, 0.12)',
    },
    {
      title: 'Total in Pipeline',
      value: formatCount(data?.total_contacted),
      iconSrc: phoneCheckIcon,
      accent: '#CB30E0',
      iconBg: 'rgba(203, 48, 224, 0.12)',
    },
  ];

  const revenueCards = [
    {
      title: 'Revenue Won',
      value: formatCurrency(data?.revenue_won),
      iconSrc: trendUpIcon,
      iconBg: 'rgba(80, 171, 61, 0.12)',
      sparkColor: '#50AB3D',
      gradientId: 'spark-won',
    },
    {
      title: 'Revenue Lost',
      value: formatCurrency(data?.revenue_lost),
      iconSrc: trendDownIcon,
      iconBg: 'rgba(255, 56, 60, 0.12)',
      sparkColor: '#FF383C',
      gradientId: 'spark-lost',
    },
    {
      title: 'Revenue In Pipeline',
      value: formatCurrency(data?.revenue_in_pipeline),
      iconSrc: funnelIcon,
      iconBg: 'rgba(238, 185, 11, 0.12)',
      sparkColor: '#EEB90B',
      gradientId: 'spark-pipeline',
    },
  ];

  return (
    <div className="flex flex-col gap-4 font-sf-pro-display">
      <div className={METRICS_GRID}>
        {summaryCards.map((card) => (
          <SummaryCard key={card.title} {...card} />
        ))}
      </div>
      <div className={METRICS_GRID}>
        {revenueCards.map((card) => (
          <RevenueCard key={card.title} {...card} />
        ))}
      </div>
    </div>
  );
}
